<template>
  <div>
    <PageHeader
      title="Constraint Analysis"
      subtitle="Kairon Intelligence · Beta — practice constraint nodes"
      :breadcrumbs="[{ label: 'Beta' }]"
    />

    <div class="p-6 space-y-4">
      <!-- HERO: does resolving a constraint action move the linked metric? -->
      <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-4">
          <div class="min-w-0">
            <h2 class="text-sm font-semibold text-gray-900">Does resolving a constraint action move the metric?</h2>
            <p class="text-[11px] text-gray-400">Resolved actions vs comparable practices — a before/after event study on the metric's pace.</p>
          </div>
          <div class="shrink-0 flex items-center gap-2">
            <label class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Metric</label>
            <FilterDropdown v-model="impactInitiative" :options="impactInitiativeOptions" />
          </div>
        </div>

        <div class="p-5">
          <LoadingIndicator v-if="impactLoading" message="Running event study…" />
          <div v-else-if="impactError" class="min-h-[120px] rounded-lg bg-red-50 p-4 text-sm text-red-800">{{ impactError }}</div>
          <div v-else-if="!impactReady" class="min-h-[120px] flex items-center justify-center text-sm text-gray-400">
            Not enough resolved actions on this metric to measure yet.
          </div>
          <div v-else class="grid grid-cols-1 lg:grid-cols-[auto_minmax(0,1fr)] gap-8 items-center">
            <div class="flex items-baseline gap-3">
              <span class="text-6xl font-semibold tabular-nums leading-none" :class="heroPositive ? 'text-emerald-600' : 'text-gray-400'">
                {{ signed(impact.did_per_100_per_year) }}
              </span>
              <span class="text-sm text-gray-500 leading-tight">{{ metricUnitLower }}<br>per 100 patients<br>per year, vs peers</span>
            </div>
            <div>
              <p v-if="heroPositive" class="text-sm text-gray-700 leading-relaxed">
                After a constraint action is resolved, practices complete about
                <span class="font-semibold">{{ Math.abs(impact.did_per_100_per_year) }} more {{ metricUnitLower }} per 100 patients each year</span>
                than comparable practices.
              </p>
              <p v-else class="text-sm text-gray-700 leading-relaxed">
                No statistically clear difference yet — resolved-action practices track their peers
                (<span class="font-medium">{{ signed(impact.did_per_100_per_year) }} {{ metricUnitLower }} per 100 patients/year</span>, not significant at this sample).
              </p>
              <div class="mt-4 flex flex-wrap gap-1.5">
                <span :class="statChip">{{ impact.did_pct_positive }}% of events positive</span>
                <span :class="statChip">p = {{ fmtP(impact.did_p_value) }}</span>
                <span :class="statChip">n = {{ impact.n_events }} events</span>
                <span :class="statChip">{{ impact.n_resolved_actions }} resolved actions</span>
              </div>
              <p class="mt-4 text-[11px] text-gray-400 leading-snug">
                Preliminary · peer-relative difference-in-differences on resolution dates · windows clamped to one
                performance year (min 1 month) · not yet placebo-tested or engagement-volume controlled.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- FILTER TOOLBAR -->
      <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div class="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-2 min-w-0">
            <span class="text-sm font-semibold text-gray-900">Constraints &amp; actions</span>
            <span class="text-[11px] text-gray-400 truncate">
              {{ filteredConstraints.length }} constraints · {{ ivStats.total }} actions ·
              <span class="text-emerald-600 font-medium">{{ ivStats.resolved }} resolved ({{ ivStats.resolvedPct }}%)</span>
            </span>
          </div>
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2">
              <label class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Group by</label>
              <FilterDropdown v-model="groupBy" :options="groupByOptions" />
            </div>
            <button v-if="hasActiveFilters" class="text-xs text-gray-500 hover:text-gray-800" @click="clearFilters">Clear all</button>
          </div>
        </div>
        <div class="px-4 py-3 flex flex-wrap gap-x-4 gap-y-3">
          <div v-for="f in filterDefs" :key="f.key" class="w-44">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">{{ f.label }}</label>
            <MultiSelectDropdown
              v-model="selected[f.key]"
              :options="f.options"
              :searchable="f.searchable"
              :placeholder="f.placeholder"
            />
          </div>
        </div>
      </section>

      <!-- BREAKDOWN PANELS: (a) constraints by category/subcategory  (b) actions by category × status -->
      <div class="space-y-4">
        <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div class="px-4 py-3 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-900">Constraints by category</h3>
            <p class="text-[11px] text-gray-400">{{ filteredConstraints.length }} constraints · subcategory breakdown</p>
          </div>
          <div class="p-4 columns-1 lg:columns-2 gap-x-10">
            <div v-if="!constraintCatSpec.groups.length" class="text-sm text-gray-400 py-8 text-center">No constraints for the current filters.</div>
            <div v-for="g in constraintCatSpec.groups" :key="g.key" class="break-inside-avoid mb-4">
              <div class="flex items-baseline gap-2 mb-1 pb-1 border-b border-gray-100">
                <span class="inline-block w-2 h-2 rounded-sm shrink-0" :style="{ background: g.headerColor }"></span>
                <span class="text-xs font-semibold text-gray-700 truncate">{{ g.label }}</span>
                <span class="flex-1"></span>
                <span class="text-[11px] tabular-nums text-gray-400">{{ g.total }}</span>
              </div>
              <div v-for="row in g.rows" :key="row.key" class="group flex items-center gap-3 py-0.5">
                <div class="w-40 shrink-0 pl-3 text-xs text-gray-600 truncate" :title="row.label">{{ row.label }}</div>
                <div class="flex-1 min-w-0 bg-gray-50 rounded">
                  <div
class="h-4 rounded transition-[width] duration-300 group-hover:brightness-95"
                       :style="{ width: Math.max(2, (row.value / constraintCatSpec.max) * 100) + '%', background: row.color }"></div>
                </div>
                <div class="w-9 shrink-0 text-xs tabular-nums font-medium text-gray-700 text-right">{{ row.value }}</div>
              </div>
            </div>
          </div>
        </section>

        <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div class="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
            <div>
              <h3 class="text-sm font-semibold text-gray-900">Actions by category &amp; status</h3>
              <p class="text-[11px] text-gray-400">{{ ivStats.total }} actions · bar length = total, segments = status</p>
            </div>
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
              <span v-for="s in ACTION_STATUS_ORDER" :key="s" class="flex items-center gap-1">
                <span class="inline-block w-2 h-2 rounded-sm" :style="{ background: actionStatusColor(s) }"></span>{{ statusLabel(s) }}
              </span>
            </div>
          </div>
          <div class="p-4 space-y-2.5">
            <div v-if="!actionStatusByCatSpec.rows.length" class="text-sm text-gray-400 py-8 text-center">No actions for the current filters.</div>
            <div v-for="row in actionStatusByCatSpec.rows" :key="row.key" class="group flex items-center gap-3">
              <div class="w-40 shrink-0 text-xs text-gray-600 truncate" :title="row.label">{{ row.label }}</div>
              <div class="flex-1 min-w-0">
                <div class="flex h-4 rounded overflow-hidden bg-gray-50" :style="{ width: Math.max(3, (row.total / actionStatusByCatSpec.max) * 100) + '%' }">
                  <div
v-for="seg in row.segs" :key="seg.status" class="h-full first:rounded-l last:rounded-r"
                       :style="{ flexGrow: seg.count, background: actionStatusColor(seg.status) }"
                       :title="statusLabel(seg.status) + ': ' + seg.count"></div>
                </div>
              </div>
              <div class="w-9 shrink-0 text-xs tabular-nums font-medium text-gray-700 text-right">{{ row.total }}</div>
            </div>
          </div>
        </section>
      </div>

      <!-- DETAIL TABLE -->
      <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div class="p-4">
          <LoadingIndicator v-if="loading" message="Loading constraints…" />
          <div
            v-else-if="error"
            class="min-h-[200px] rounded-lg bg-red-50 p-4 text-sm text-red-800"
          >
            {{ error }}
          </div>
          <div v-else class="border border-gray-200 rounded-lg overflow-auto max-h-[720px]">
            <template v-for="group in groupedRows" :key="group.key">
              <div
                v-if="group.label !== null"
                class="sticky top-0 z-10 bg-gray-100/95 backdrop-blur border-b border-gray-200 px-4 py-1.5 text-xs font-semibold text-gray-700"
              >
                <span :class="groupBy === 'practice' ? 'sensitive' : ''">{{ group.label }}</span>
                <span class="text-gray-400 font-normal">· {{ group.rows.length }}</span>
              </div>
              <div v-for="constraint in group.rows" :key="constraint.id" class="border-b border-gray-100">
                <!-- summary: constraint (left) | actions preview (right) -->
                <div
                  class="grid grid-cols-[1.5fr_1fr] gap-4 px-4 py-2.5 cursor-pointer hover:bg-gray-50"
                  @click="toggleExpand(constraint.id)"
                >
                  <div class="min-w-0 flex items-start gap-2">
                    <span class="mt-0.5 shrink-0 text-gray-400" aria-hidden="true">{{ isExpanded(constraint.id) ? '▾' : '▸' }}</span>
                    <div class="min-w-0">
                      <div class="text-sm font-medium truncate sensitive" data-demo-blur :class="isDimmed(constraint) ? 'text-gray-400' : 'text-gray-900'">
                        {{ constraint.title }}
                      </div>
                      <div class="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px]">
                        <span v-if="constraint.category" :class="badgeBase" :style="categoryStyle(constraint.category)">{{ categoryLabel(constraint.category) }}</span>
                        <span :class="badgeBase" :style="statusStyleFor(constraint.status)">
                          <span aria-hidden="true" class="leading-none">{{ statusGlyph(constraint.status) }}</span>{{ statusLabel(constraint.status) }}
                        </span>
                        <span class="text-gray-400">{{ initiativeLabel(constraint.initiative_area) }}</span>
                        <span v-if="constraint.owner_name" class="text-gray-400 truncate sensitive">· {{ constraint.owner_name }}</span>
                        <span v-if="groupBy !== 'practice'" class="text-gray-400 truncate sensitive">· {{ constraint.practice_name }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="min-w-0 border-l border-gray-100 pl-4">
                    <div v-if="(constraint.interventions || []).length" class="space-y-1">
                      <div class="flex items-center justify-between text-[10px] uppercase tracking-wide text-gray-400">
                        <span>Actions</span>
                        <span class="tabular-nums">
                          {{ constraint.intervention_count }}<span v-if="constraint.resolved_intervention_count" class="text-emerald-600"> · {{ constraint.resolved_intervention_count }} resolved</span>
                        </span>
                      </div>
                      <div
                        v-for="iv in constraint.interventions.slice(0, 3)"
                        :key="iv.id"
                        class="flex items-center gap-1.5 text-xs text-gray-600 min-w-0"
                      >
                        <span class="shrink-0 leading-none" :style="{ color: statusStyleFor(iv.status).color }" aria-hidden="true">{{ statusGlyph(iv.status) }}</span>
                        <span v-if="iv.owner_group" :class="badgeBase" :style="ownerGroupStyle(iv.owner_group)">{{ ownerGroupLabel(iv.owner_group) }}</span>
                        <span class="truncate sensitive" data-demo-blur>{{ iv.title }}</span>
                      </div>
                      <div v-if="constraint.interventions.length > 3" class="text-[11px] text-gray-400">
                        +{{ constraint.interventions.length - 3 }} more
                      </div>
                    </div>
                    <div v-else class="text-xs text-gray-300">No actions yet</div>
                  </div>
                </div>

                <!-- expanded: all constraint fields + full actions -->
                <div v-if="isExpanded(constraint.id)" class="border-t border-gray-100 bg-gray-50/70 px-4 pt-2 pb-3">
                  <dl class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-2 pl-6 mb-3 text-xs">
                    <div><dt class="text-gray-400">Subcategory</dt><dd class="text-gray-700">{{ subcategoryLabel(constraint.subcategory) }}</dd></div>
                    <div><dt class="text-gray-400">Gap type</dt><dd><span v-if="constraint.gap_type" :class="badgeBase" :style="gapTypeStyle(constraint.gap_type)">{{ gapTypeLabel(constraint.gap_type) }}</span><span v-else class="text-gray-400">not actionable</span></dd></div>
                    <div><dt class="text-gray-400">Scope</dt><dd class="text-gray-700"><span :class="constraint.scope === 'provider' ? 'sensitive' : ''">{{ constraint.scope === 'provider' ? (constraint.subject_provider_name || 'Provider') : 'Practice' }}</span></dd></div>
                    <div><dt class="text-gray-400">Disposition</dt><dd><span v-if="constraint.disposition" :class="badgeBase" :style="dispositionStyle(constraint.disposition)">{{ dispositionLabel(constraint.disposition) }}</span><span v-else class="text-gray-400">—</span></dd></div>
                    <div><dt class="text-gray-400">Severity</dt><dd><span v-if="constraint.severity" :class="badgeBase" :style="severityStyle(constraint.severity)">{{ constraint.severity }}</span><span v-else class="text-gray-400">—</span></dd></div>
                    <div><dt class="text-gray-400">Raised</dt><dd class="text-gray-600 tabular-nums">{{ formatDate(constraint.raised_at) }}</dd></div>
                    <div><dt class="text-gray-400">Last seen</dt><dd class="text-gray-600 tabular-nums">{{ formatDate(constraint.last_seen_at) }}</dd></div>
                    <div><dt class="text-gray-400">Closed</dt><dd class="text-gray-600 tabular-nums">{{ formatDate(constraint.closed_at) }}</dd></div>
                    <div><dt class="text-gray-400">Mentions</dt><dd class="text-gray-600 tabular-nums">{{ constraint.mention_count }}</dd></div>
                    <div v-if="groupBy === 'practice'"><dt class="text-gray-400">Practice</dt><dd class="text-gray-600 truncate sensitive">{{ constraint.practice_name }}</dd></div>
                  </dl>

                  <div class="pl-6">
                    <div class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      Actions — what's being done<span v-if="(constraint.interventions || []).length" class="font-normal text-gray-300"> · {{ constraint.interventions.length }}</span>
                    </div>
                    <div v-if="!(constraint.interventions || []).length" class="text-xs text-gray-400">No actions recorded yet.</div>
                    <div
                      v-for="iv in constraint.interventions"
                      :key="iv.id"
                      class="py-1.5 border-t border-gray-100 first:border-t-0"
                    >
                      <div class="flex items-center gap-2 flex-wrap">
                        <span :class="badgeBase" :style="statusStyleFor(iv.status)">
                          <span aria-hidden="true" class="leading-none">{{ statusGlyph(iv.status) }}</span>{{ statusLabel(iv.status) }}
                        </span>
                        <span v-if="iv.action_type" :class="badgeBase" :style="actionTypeStyle()">{{ actionTypeLabel(iv.action_type) }}</span>
                        <span v-if="iv.owner_group" :class="badgeBase" :style="ownerGroupStyle(iv.owner_group)">{{ ownerGroupLabel(iv.owner_group) }}</span>
                        <span class="text-sm text-gray-800 sensitive" data-demo-blur>{{ iv.title }}</span>
                        <span v-if="iv.owner_name" class="text-xs text-gray-400 sensitive">· {{ iv.owner_name }}</span>
                        <span class="flex-1"></span>
                        <span class="text-xs text-gray-400 tabular-nums whitespace-nowrap">
                          {{ formatDate(iv.opened_at) }}<template v-if="iv.closed_at"> → {{ formatDate(iv.closed_at) }}</template>
                        </span>
                        <span class="text-xs text-gray-400 tabular-nums">{{ iv.mention_count }}×</span>
                      </div>
                      <div v-if="iv.description" class="mt-0.5 ml-0.5 text-xs text-gray-500 sensitive" data-demo-blur>{{ iv.description }}</div>
                      <div v-if="(iv.status_changes || []).length" class="mt-1 ml-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-400">
                        <template v-for="(sc, i) in iv.status_changes" :key="i">
                          <span class="flex items-center gap-1">
                            <span class="leading-none" :style="{ color: statusStyleFor(sc.status).color }" aria-hidden="true">{{ statusGlyph(sc.status) }}</span>
                            {{ statusLabel(sc.status) }} <span class="tabular-nums">{{ formatDate(sc.date) }}</span>
                          </span>
                          <span v-if="i < iv.status_changes.length - 1" class="text-gray-300" aria-hidden="true">→</span>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <div v-if="!filteredConstraints.length" class="px-4 py-8 text-center text-sm text-gray-400">
              No constraints match the current filters.
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import MultiSelectDropdown from '@/components/widgetcomponents/library/MultiSelectDropdown.vue';
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue';
import {
  fmt, HEALTH, NEUTRAL, severityStyle, STATUS_OPTIONS, INITIATIVE_ORDER,
} from '@/components/EngagementTracker/engagementTrackerHelpers.js';

// The lifecycle axis (renamed from "category"). Order + colors as before.
const DISPOSITION_ORDER = ['unaddressed', 'being_worked', 'worked_around', 'fixed', 'unable', 'abandoned'];

const NEUTRAL_STYLE = { background: NEUTRAL.tint, color: NEUTRAL.ink };

// Softer bar fills that read like the table's tag pills, not saturated blocks: mix the tag's ink
// color toward white so it stays the same hue family but calmer.
function soften(hex, mix = 0.5) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const to = v => Math.round(v + (255 - v) * mix).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

const DISPOSITION_STYLE = {
  unaddressed: { background: NEUTRAL.tint, color: NEUTRAL.ink },
  being_worked: { background: HEALTH.on_track.tint, color: HEALTH.on_track.hex },
  worked_around: { background: '#FEF3C7', color: '#B45309' },
  fixed: { background: HEALTH.ahead.tint, color: HEALTH.ahead.hex },
  unable: { background: HEALTH.behind.tint, color: HEALTH.behind.hex },
  abandoned: { background: NEUTRAL.tint, color: '#9ca3af' },
};

// The subject axis (the 9 controlled categories). One calm color each.
const CATEGORY_STYLE = {
  data_integrity: { background: '#EFF6FF', color: '#1D4ED8' },
  coding: { background: '#F5F3FF', color: '#6D28D9' },
  tooling_access: { background: '#ECFEFF', color: '#0E7490' },
  transitions_of_care: { background: '#FEF2F2', color: '#B91C1C' },
  scheduling: { background: '#FFF7ED', color: '#C2410C' },
  capacity: { background: '#FEFCE8', color: '#A16207' },
  patient: { background: '#F0FDF4', color: '#15803D' },
  utilization_referrals: { background: '#FDF4FF', color: '#A21CAF' },
  payment_contract: { background: '#F1F5F9', color: '#334155' },
};

// The mechanism axis (why the work isn't happening).
const GAP_TYPE_STYLE = {
  knowledge: { background: '#EFF6FF', color: '#1D4ED8' },
  capacity: { background: '#FFF7ED', color: '#C2410C' },
  ownership: { background: '#F5F3FF', color: '#6D28D9' },
  motivation: { background: '#FDF2F8', color: '#BE185D' },
  other: { background: '#F1F5F9', color: '#475569' },
};

const STATUS_STYLE = {
  open: { background: '#f3f4f6', color: '#4b5563' },
  in_progress: { background: '#e5e7eb', color: '#374151' },
  resolved: { background: '#f3f4f6', color: '#9ca3af' },
  dropped: { background: '#f3f4f6', color: '#9ca3af' },
};

// Constraint-action owner group — who is delivering the fix (ACO/Kairon care team vs the practice).
const OWNER_GROUP_STYLE = {
  care_team: { background: '#EFF6FF', color: '#1D4ED8' },
  practice: { background: '#F0FDF4', color: '#15803D' },
};
const OWNER_GROUP_LABELS = { care_team: 'Care team', practice: 'Practice' };
const ACTION_TYPE_STYLE = { background: '#EEF2FF', color: '#4338CA' };

// Action-status colors for the stacked "actions by category" bars — resolved pops (the fixing input
// is confirmed), the rest recede. Distinct from the muted table pills so the stack reads at a glance.
const ACTION_STATUS_ORDER = ['resolved', 'in_progress', 'open', 'dropped'];
const ACTION_STATUS_COLOR = {
  resolved: soften(HEALTH.ahead.hex, 0.5),
  in_progress: soften('#2563EB', 0.55),
  open: '#E2E8F0',
  dropped: '#EEF1F4',
};

// initiative_area is stored as a raw key; humanize to match the Engagement Tracker's spine labels.
const INITIATIVE_LABELS = {
  awv_completion: 'AWV Completion',
  visit_coverage: 'Visit Coverage',
  risk_documentation: 'Risk Documentation',
  transitions_of_care: 'Transitions of Care',
  contract_payments: 'Contract & Payments',
};

// Metric-impact hero — the initiatives that carry composable metrics we can run the study on, and
// the noun the metric counts (for the hero copy). Others fall back to a generic unit.
const IMPACT_INITIATIVES = [
  { value: 'awv_completion', label: 'AWV Completion', unit: 'AWVs' },
  { value: 'visit_coverage', label: 'Visit Coverage', unit: 'Visits' },
  { value: 'risk_documentation', label: 'Risk Documentation', unit: 'Conditions' },
  { value: 'transitions_of_care', label: 'Transitions of Care', unit: 'Follow-ups' },
];

const STATUS_RANK = STATUS_OPTIONS.reduce((map, status, i) => {
  map[status] = i;
  return map;
}, {});

// Table "Group by" options (single-select — rendered with the shared FilterDropdown so it matches
// the filter dropdowns instead of a native OS <select>).
const GROUP_BY_OPTIONS = [
  { value: 'practice', label: 'Practice' },
  { value: 'category', label: 'Category' },
  { value: 'subcategory', label: 'Subcategory' },
  { value: 'gap_type', label: 'Gap type' },
  { value: 'disposition', label: 'Disposition' },
  { value: 'scope', label: 'Scope' },
  { value: 'status', label: 'Status' },
  { value: 'none', label: 'None' },
];

// Field on each constraint row backing each filterable dimension.
const DIMENSIONS = [
  { key: 'practice', field: 'practice_entity_id' },
  { key: 'category', field: 'category' },
  { key: 'subcategory', field: 'subcategory' },
  { key: 'gap_type', field: 'gap_type' },
  { key: 'disposition', field: 'disposition' },
  { key: 'scope', field: 'scope' },
  { key: 'status', field: 'status' },
  { key: 'initiative', field: 'initiative_area' },
];

export default {
  name: 'ConstraintAnalysis',
  components: { PageHeader, LoadingIndicator, MultiSelectDropdown, FilterDropdown },
  data() {
    return {
      loading: false,
      error: null,
      practices: [],
      constraints: [],
      initiatives: [],
      expanded: {},   // constraint id -> true when its interventions sub-row is open
      taxonomy: {
        categories: [],
        other: { key: 'other', label: 'Other' },
        dispositions: [],
        gap_types: [],
        scopes: [],
      },
      selected: {
        practice: [],
        category: [],
        subcategory: [],
        gap_type: [],
        disposition: [],
        scope: [],
        status: [],
        initiative: [],
      },
      groupBy: 'practice',
      groupByOptions: GROUP_BY_OPTIONS,
      impact: null,
      impactLoading: false,
      impactError: null,
      impactInitiative: 'awv_completion',
      impactInitiativeOptions: IMPACT_INITIATIVES.map(i => ({ value: i.value, label: i.label })),
      ACTION_STATUS_ORDER,
      badgeBase: 'inline-flex items-center gap-1 rounded-full text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 whitespace-nowrap',
      statChip: 'inline-flex items-center rounded-full bg-gray-100 text-gray-600 text-[11px] font-medium px-2 py-0.5',
    };
  },
  computed: {
    toast() {
      return useToast();
    },
    categoryOrder() {
      return this.taxonomy.categories.map(c => c.key);
    },
    categoryLabelMap() {
      const map = {};
      for (const c of this.taxonomy.categories) map[c.key] = c.label;
      return map;
    },
    subcategoryLabelMap() {
      const map = { [this.taxonomy.other.key]: this.taxonomy.other.label };
      for (const c of this.taxonomy.categories) {
        for (const s of c.subcategories) map[s.key] = s.label;
      }
      return map;
    },
    subcategoryOrder() {
      const order = [];
      for (const c of this.taxonomy.categories) {
        for (const s of c.subcategories) order.push(s.key);
      }
      order.push(this.taxonomy.other.key);
      return order;
    },
    practiceOptions() {
      return this.practices.map(practice => ({
        value: practice.entity_id,
        label: practice.practice_name,
      }));
    },
    categoryOptions() {
      return this.presentValues('category', this.categoryOrder).map(value => ({
        value,
        label: this.categoryLabel(value),
      }));
    },
    subcategoryOptions() {
      return this.presentValues('subcategory', this.subcategoryOrder).map(value => ({
        value,
        label: this.subcategoryLabel(value),
      }));
    },
    gapTypeOptions() {
      return this.presentValues('gap_type', this.taxonomy.gap_types).map(value => ({
        value,
        label: this.gapTypeLabel(value),
      }));
    },
    dispositionOptions() {
      return this.presentValues('disposition', DISPOSITION_ORDER).map(value => ({
        value,
        label: this.dispositionLabel(value),
      }));
    },
    scopeOptions() {
      return this.presentValues('scope', this.taxonomy.scopes).map(value => ({
        value,
        label: this.scopeLabel(value),
      }));
    },
    statusOptions() {
      return this.presentValues('status', STATUS_OPTIONS).map(value => ({
        value,
        label: this.statusLabel(value),
      }));
    },
    initiativeOptions() {
      return this.presentValues('initiative_area', INITIATIVE_ORDER).map(value => ({
        value,
        label: this.initiativeLabel(value),
      }));
    },
    // The 7 filter dropdowns, driven off one list so the toolbar stays a single grid.
    filterDefs() {
      return [
        { key: 'practice', label: 'Practice', options: this.practiceOptions, searchable: true, placeholder: 'All practices' },
        { key: 'category', label: 'Category', options: this.categoryOptions, searchable: false, placeholder: 'All categories' },
        { key: 'subcategory', label: 'Subcategory', options: this.subcategoryOptions, searchable: true, placeholder: 'All subcategories' },
        { key: 'gap_type', label: 'Gap type', options: this.gapTypeOptions, searchable: false, placeholder: 'All gap types' },
        { key: 'disposition', label: 'Disposition', options: this.dispositionOptions, searchable: false, placeholder: 'All dispositions' },
        { key: 'scope', label: 'Scope', options: this.scopeOptions, searchable: false, placeholder: 'All scopes' },
        { key: 'status', label: 'Status', options: this.statusOptions, searchable: false, placeholder: 'All statuses' },
      ];
    },
    hasActiveFilters() {
      return DIMENSIONS.some(d => this.selected[d.key].length > 0);
    },
    filteredConstraints() {
      return this.constraints.filter(constraint => this.matches(constraint));
    },
    // Every intervention under the currently-filtered constraints, carrying its parent's category so
    // the panels can pivot on it.
    filteredInterventions() {
      const out = [];
      for (const p of this.filteredConstraints) {
        for (const iv of (p.interventions || [])) {
          out.push({ ...iv, constraint_category: p.category });
        }
      }
      return out;
    },
    // Action headline — total + lifecycle counts (resolved = the fixing input is confirmed).
    ivStats() {
      const by = { open: 0, in_progress: 0, resolved: 0, dropped: 0 };
      for (const iv of this.filteredInterventions) {
        if (iv.status in by) by[iv.status] += 1;
      }
      const total = this.filteredInterventions.length;
      return { total, ...by, resolvedPct: total ? Math.round((by.resolved / total) * 100) : 0 };
    },
    // Panel (a): constraints grouped by category, each category's rows its subcategories.
    constraintCatSpec() {
      const byCat = new Map();
      const catTotals = new Map();
      for (const p of this.filteredConstraints) {
        const cat = p.category || '__uncat__';
        const sub = p.subcategory || '__null__';
        if (!byCat.has(cat)) byCat.set(cat, new Map());
        const m = byCat.get(cat);
        m.set(sub, (m.get(sub) || 0) + 1);
        catTotals.set(cat, (catTotals.get(cat) || 0) + 1);
      }
      const ordered = this.categoryOrder.filter(k => byCat.has(k));
      if (byCat.has('__uncat__')) ordered.push('__uncat__');
      let max = 1;
      const groups = ordered.map(catKey => {
        const isUncat = catKey === '__uncat__';
        const color = soften(isUncat ? NEUTRAL.hex : (CATEGORY_STYLE[catKey] || NEUTRAL_STYLE).color);
        const rows = [...byCat.get(catKey).entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([sub, count]) => {
            max = Math.max(max, count);
            return {
              key: `${catKey}|${sub}`,
              label: sub === '__null__' ? '—' : this.subcategoryLabel(sub),
              value: count,
              color,
            };
          });
        return {
          key: catKey,
          label: isUncat ? 'Uncategorized' : this.categoryLabel(catKey),
          headerColor: color,
          total: catTotals.get(catKey),
          rows,
        };
      }).sort((a, b) => b.total - a.total);
      return { groups, max };
    },
    // Panel (b): actions grouped by their constraint's category, each a stacked status bar.
    actionStatusByCatSpec() {
      const byCat = new Map();
      for (const iv of this.filteredInterventions) {
        const cat = iv.constraint_category || '__none__';
        if (!byCat.has(cat)) byCat.set(cat, { open: 0, in_progress: 0, resolved: 0, dropped: 0, total: 0 });
        const m = byCat.get(cat);
        if (iv.status in m) m[iv.status] += 1;
        m.total += 1;
      }
      const ordered = this.categoryOrder.filter(k => byCat.has(k));
      if (byCat.has('__none__')) ordered.push('__none__');
      const max = Math.max(1, ...[...byCat.values()].map(m => m.total));
      const rows = ordered
        .map(cat => {
          const m = byCat.get(cat);
          return {
            key: cat,
            label: cat === '__none__' ? '—' : this.categoryLabel(cat),
            total: m.total,
            segs: ACTION_STATUS_ORDER.filter(s => m[s]).map(s => ({ status: s, count: m[s] })),
          };
        })
        .sort((a, b) => b.total - a.total);
      return { rows, max };
    },
    impactReady() {
      return !!(this.impact && this.impact.n_events && !this.impact.underpowered);
    },
    metricUnit() {
      const found = IMPACT_INITIATIVES.find(i => i.value === this.impactInitiative);
      return found ? found.unit : 'Units';
    },
    metricUnitLower() {
      return this.metricUnit.toLowerCase();
    },
    // The hero leads with a positive claim only when the lift is significant AND positive; otherwise
    // the number is muted and the copy hedges (a positive-but-not-significant estimate isn't proof).
    heroPositive() {
      return !!(this.impact && this.impact.significant && this.impact.did_per_100_per_year > 0);
    },
    sortedConstraints() {
      return [...this.filteredConstraints].sort((a, b) => {
        const byPractice = (a.practice_name || '').localeCompare(b.practice_name || '');
        if (byPractice !== 0) return byPractice;
        return (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
      });
    },
    groupedRows() {
      if (this.groupBy === 'none') {
        return [{ key: 'all', label: null, rows: this.sortedConstraints }];
      }
      const groups = [];
      const byKey = {};
      for (const constraint of this.sortedConstraints) {
        const { key, label } = this.groupKeyFor(constraint);
        if (!byKey[key]) {
          byKey[key] = { key, label, rows: [] };
          groups.push(byKey[key]);
        }
        byKey[key].rows.push(constraint);
      }
      return groups;
    },
  },
  watch: {
    impactInitiative() {
      this.loadImpact();
    },
  },
  mounted() {
    this.loadData();
    this.loadImpact();
  },
  methods: {
    severityStyle,
    statusLabel: fmt.statusLabel,
    statusGlyph: fmt.statusGlyph,
    formatDate: fmt.formatDate,
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('api/engagement-graph-nodes/constraints/');
        this.practices = data.practices || [];
        this.constraints = data.constraints || [];
        this.initiatives = data.initiatives || [];
        if (data.taxonomy) this.taxonomy = { ...this.taxonomy, ...data.taxonomy };
      } catch (error) {
        this.error = error?.response?.data?.error || error.message;
        this.toast.error(this.error);
      } finally {
        this.loading = false;
      }
    },
    async loadImpact() {
      this.impactLoading = true;
      this.impactError = null;
      try {
        const { data } = await axios.get('api/engagement-graph-nodes/action-metric-impact/', {
          params: { initiative: this.impactInitiative },
        });
        this.impact = data;
      } catch (error) {
        this.impactError = error?.response?.data?.error || error.message;
      } finally {
        this.impactLoading = false;
      }
    },
    matches(constraint) {
      for (const d of DIMENSIONS) {
        const sel = this.selected[d.key];
        if (sel.length && !sel.includes(constraint[d.field])) return false;
      }
      return true;
    },
    presentValues(field, canonical) {
      const order = canonical || [];
      const present = new Set(this.constraints.map(constraint => constraint[field]).filter(Boolean));
      const known = order.filter(value => present.has(value));
      const extra = [...present].filter(value => !order.includes(value)).sort((a, b) => a.localeCompare(b));
      return [...known, ...extra];
    },
    groupKeyFor(constraint) {
      if (this.groupBy === 'practice') {
        return { key: constraint.practice_entity_id, label: constraint.practice_name };
      }
      if (this.groupBy === 'category') {
        return { key: constraint.category || '—', label: this.categoryLabel(constraint.category) };
      }
      if (this.groupBy === 'subcategory') {
        return { key: constraint.subcategory || '—', label: this.subcategoryLabel(constraint.subcategory) };
      }
      if (this.groupBy === 'gap_type') {
        return {
          key: constraint.gap_type || '__na__',
          label: constraint.gap_type ? this.gapTypeLabel(constraint.gap_type) : 'Not actionable',
        };
      }
      if (this.groupBy === 'scope') {
        return { key: constraint.scope || '—', label: this.scopeLabel(constraint.scope) };
      }
      if (this.groupBy === 'status') {
        return { key: constraint.status, label: this.statusLabel(constraint.status) };
      }
      return { key: constraint.disposition || '—', label: this.dispositionLabel(constraint.disposition) };
    },
    clearFilters() {
      for (const d of DIMENSIONS) this.selected[d.key] = [];
    },
    signed(value) {
      const n = Number(value) || 0;
      return (n > 0 ? '+' : '') + n;
    },
    fmtP(p) {
      if (p == null) return '—';
      return p < 0.001 ? '<0.001' : Number(p).toFixed(3);
    },
    actionStatusColor(status) {
      return ACTION_STATUS_COLOR[status] || '#E5E7EB';
    },
    labelize(value) {
      if (!value) return '—';
      return value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    categoryLabel(value) {
      if (!value) return '—';
      return this.categoryLabelMap[value] || this.labelize(value);
    },
    subcategoryLabel(value) {
      if (!value) return '—';
      return this.subcategoryLabelMap[value] || this.labelize(value);
    },
    gapTypeLabel(value) {
      return this.labelize(value);
    },
    dispositionLabel(value) {
      return this.labelize(value);
    },
    scopeLabel(value) {
      return this.labelize(value);
    },
    initiativeLabel(value) {
      if (!value) return '—';
      return INITIATIVE_LABELS[value] || this.labelize(value);
    },
    categoryStyle(category) {
      return CATEGORY_STYLE[category] || NEUTRAL_STYLE;
    },
    gapTypeStyle(gapType) {
      return GAP_TYPE_STYLE[gapType] || NEUTRAL_STYLE;
    },
    dispositionStyle(disposition) {
      return DISPOSITION_STYLE[disposition] || NEUTRAL_STYLE;
    },
    statusStyleFor(status) {
      return STATUS_STYLE[status] || NEUTRAL_STYLE;
    },
    ownerGroupStyle(side) {
      return OWNER_GROUP_STYLE[side] || NEUTRAL_STYLE;
    },
    ownerGroupLabel(side) {
      return OWNER_GROUP_LABELS[side] || this.labelize(side);
    },
    actionTypeStyle() {
      return ACTION_TYPE_STYLE;
    },
    actionTypeLabel(value) {
      return this.labelize(value);
    },
    toggleExpand(id) {
      this.expanded[id] = !this.expanded[id];
    },
    isExpanded(id) {
      return !!this.expanded[id];
    },
    isDimmed(constraint) {
      return constraint.status === 'resolved' || constraint.status === 'dropped';
    },
  },
};
</script>
